import { useState } from "react";
import { mediaUrl, shuffleStable } from "../common";
import { useSlideState, addTry, useReport, useReviewMode } from "../answers";
import { LuBadgeCheck, LuHeart, LuMessageCircle, LuMoreHorizontal, LuRepeat2, LuSend } from "react-icons/lu";
import { Marked } from "./extra";
import * as s from "./rich.styled";

// «Живі» блоки (2026-10): макети реальних речей, підказки, картки-факти, кольорові формули й шкала часу, кошики, «знайди помилку».

// ---------- макети для reading: data.mock = "post" | "email" | "menu" | "reviews" ----------
// post:    data.post = { author, place?, avatar?, image, text: [абзаци], likes, time, comments?: [{ name, text }] }
// email:   data.email = { from, to, subject, date?, body: [абзаци] }
// menu:    data.menu = { name, subtitle?, sections: [{ title, items: [{ name, desc?, price }] }] }
// reviews: data.reviews = { place, rating, items: [{ name, stars, date?, text }] }
export function Mock({ data }) {
  // thread: data.thread = { author, verified?, time, text: [абзаци], likes, replies, reposts } — пост у стилі Threads (без фото, великий шрифт)
  if (data.mock === "thread") {
    const t = data.thread || {};
    return (
      <s.MockWrap>
        <s.Thread>
          <s.ThreadAvatar>{(t.author || "?")[0].toUpperCase()}</s.ThreadAvatar>
          <div>
            <s.ThreadHead>
              <b>{t.author}</b>
              {t.verified && <LuBadgeCheck size={17} />}
              <span>{t.time}</span>
              <LuMoreHorizontal size={20} />
            </s.ThreadHead>
            {(t.text || []).map((x, i) => (
              <p key={i}>
                <Marked text={x} />
              </p>
            ))}
            <s.ThreadActions>
              <span><LuHeart size={20} /> {t.likes}</span>
              <span><LuMessageCircle size={20} /> {t.replies}</span>
              <span><LuRepeat2 size={20} /> {t.reposts}</span>
              <span><LuSend size={20} /></span>
            </s.ThreadActions>
          </div>
        </s.Thread>
      </s.MockWrap>
    );
  }
  if (data.mock === "post") {
    const p = data.post || {};
    return (
      <s.MockWrap>
        <s.Post>
          <s.PostHead>
            <s.Avatar>
              <div style={p.avatar ? { backgroundImage: `url(${mediaUrl(p.avatar)})` } : undefined}>{!p.avatar && (p.author || "?")[0]}</div>
            </s.Avatar>
            <div>
              <b>{p.author}</b>
              {p.place && <span> · {p.place}</span>}
            </div>
          </s.PostHead>
          {p.image && <s.PostImage src={mediaUrl(p.image)} alt="" />}
          <s.PostIcons>
            <span>♡</span>
            <span>💬</span>
            <span>➤</span>
            <span>🔖</span>
          </s.PostIcons>
          <s.PostText>
            {p.likes && <b>{p.likes} likes</b>}
            {(p.text || []).map((t, i) => (
              <p key={i}>
                {i === 0 && <b>{p.author} </b>}
                <Marked text={t} />
              </p>
            ))}
            {(p.comments || []).map((c, i) => (
              <p key={`c${i}`}>
                <b>{c.name} </b>
                <Marked text={c.text} />
              </p>
            ))}
            {p.time && <small>{p.time}</small>}
          </s.PostText>
        </s.Post>
      </s.MockWrap>
    );
  }
  if (data.mock === "email") {
    const m = data.email || {};
    return (
      <s.Mail>
        <s.MailBar>
          <i style={{ background: "#ff5f57" }} />
          <i style={{ background: "#febc2e" }} />
          <i style={{ background: "#28c840" }} />
          <b>Inbox</b>
        </s.MailBar>
        <s.MailHead>
          <h3>{m.subject}</h3>
          <div>
            <span>From: </span>
            {m.from}
          </div>
          <div>
            <span>To: </span>
            {m.to}
            {m.date && <span style={{ float: "right" }}>{m.date}</span>}
          </div>
        </s.MailHead>
        <s.MailBody>
          {(m.body || []).map((t, i) => (
            <p key={i}>
              <Marked text={t} />
            </p>
          ))}
        </s.MailBody>
      </s.Mail>
    );
  }
  if (data.mock === "menu") {
    const m = data.menu || {};
    return (
      <s.Menu>
        <h3>{m.name}</h3>
        {m.subtitle && <small>{m.subtitle}</small>}
        {(m.sections || []).map((sec) => (
          <div key={sec.title}>
            <h4>{sec.title}</h4>
            {sec.items.map((it) => (
              <s.MenuItem key={it.name}>
                <b>{it.name}</b>
                <span>{it.price}</span>
                {it.desc && <small>{it.desc}</small>}
              </s.MenuItem>
            ))}
          </div>
        ))}
      </s.Menu>
    );
  }
  if (data.mock === "reviews") {
    const r = data.reviews || {};
    const stars = (n) => "★★★★★".slice(0, n) + "☆☆☆☆☆".slice(0, 5 - n);
    return (
      <s.Reviews>
        <s.ReviewsHead>
          <h3>{r.place}</h3>
          <span>{stars(Math.round(r.rating || 0))}</span>
          <b>{r.rating}</b>
        </s.ReviewsHead>
        {(r.items || []).map((it, i) => (
          <s.Review key={i}>
            <header>
              <b>{it.name}</b>
              <span>{stars(it.stars)}</span>
              {it.date && <small>{it.date}</small>}
            </header>
            <Marked text={it.text} />
          </s.Review>
        ))}
      </s.Reviews>
    );
  }
  return null;
}

// ---------- підказка у шапці будь-якого слайда: slide.tip = { kind: "mistake" | "hack" | "fact", text, back? } ----------
// back — переклад/пояснення: клік перевертає підказку
const TIP = { mistake: ["⚠️", "Common mistake"], hack: ["💡", "Lifehack"], fact: ["🌍", "Did you know?"] };
export function Tip({ tip }) {
  const [flipped, setFlipped] = useState(false);
  if (!tip?.text) return null;
  const [icon, label] = TIP[tip.kind] || TIP.hack;
  return (
    <s.Tip type="button" $kind={tip.kind} $flip={Boolean(tip.back)} onClick={() => tip.back && setFlipped((f) => !f)}>
      <em>{icon}</em>
      <span>
        <i>{label}</i>
        <Marked text={flipped ? tip.back : tip.text} />
        {tip.back && <small>{flipped ? "↺ English" : "↺ Переклад"}</small>}
      </span>
    </s.Tip>
  );
}

// ---------- картки-факти: type "facts", data.cards = [{ label?, front, back, image? }] — клік перевертає (англ. → переклад) ----------
function Facts({ slide }) {
  const { cards = [] } = slide.data || {};
  const [flipped, setFlipped] = useState({});
  return (
    <s.Facts $count={cards.length}>
      {cards.map((card, i) => (
        <s.Fact key={i} type="button" $flipped={flipped[i]} onClick={() => setFlipped((f) => ({ ...f, [i]: !f[i] }))}>
          <s.FactFront>
            {card.image && <img src={mediaUrl(card.image)} alt="" />}
            <div>
              <i>{card.label || "Did you know?"}</i>
              <Marked text={card.front} />
            </div>
            <small>↺ click</small>
          </s.FactFront>
          <s.FactBack>
            <div>
              <i>Переклад</i>
              <Marked text={card.back} />
            </div>
          </s.FactBack>
        </s.Fact>
      ))}
    </s.Facts>
  );
}

// ---------- граматика: data.formula = [{ parts: [[текст, роль, підпис?]], example }] ролі: subj aux verb end neg obj time ----------
//            data.timeline = { labels: [{ at: 0..100, text }], marks: [{ at, text, color? }], spans: [{ from, to, color? }] }
export function GrammarVisual({ data }) {
  const { formula = [], timeline } = data || {};
  return (
    <>
      {formula.length > 0 && (
        <s.Formulas>
          {formula.map((f, i) => (
            <s.Formula key={i} $i={i}>
              {f.parts.map(([text, role, sub], j) => (
                <span key={j} style={{ display: "contents" }}>
                  {j > 0 && <i>+</i>}
                  <s.Chip $role={role}>
                    {text}
                    {sub && <small>{sub}</small>}
                  </s.Chip>
                </span>
              ))}
              {f.example && (
                <em>
                  <Marked text={f.example} />
                </em>
              )}
            </s.Formula>
          ))}
        </s.Formulas>
      )}
      {timeline && (
        <s.Timeline>
          {(timeline.spans || []).map((sp, i) => (
            <s.TimeSpan key={`s${i}`} $from={sp.from} $to={sp.to} $color={sp.color} />
          ))}
          {(timeline.labels || []).map((l) => (
            <s.TimeLabel key={l.text} $at={l.at}>
              {l.text}
            </s.TimeLabel>
          ))}
          {(timeline.marks || []).map((m, i) => (
            <s.TimeMark key={m.text} $at={m.at} $i={i} $color={m.color}>
              <span>{m.text}</span>
              <i />
            </s.TimeMark>
          ))}
        </s.Timeline>
      )}
    </>
  );
}

// ---------- кошики: type "buckets", data = { buckets: ["/t/", "/d/"], items: [{ text, bucket }] } ----------
// Клік по слову → клік по кошику (або перетягнути). Правильно — слово лягає в кошик; ні — трясеться (спроба записується).
function Buckets({ slide, showAnswers }) {
  const { buckets = [], items = [] } = slide.data || {};
  const [placed, setPlaced] = useSlideState(slide.id, "placed", {});
  const [tries, setTries] = useSlideState(slide.id, "tries", {});
  const review = useReviewMode();
  useReport(slide.id, Object.keys(placed).length, items.length, Object.keys(placed).length);
  const [picked, setPicked] = useState(null);
  const [wrong, setWrong] = useState(null);
  const [over, setOver] = useState(null);
  // перемішуємо за текстом (числа 0..n майже не перемішуються хешем — слова лишались би по групах)
  const order = shuffleStable(items.map((it, i) => ({ t: it.text, i })), slide.id).map((x) => x.i);
  const isIn = (i) => showAnswers || placed[i] != null;

  const drop = (i, b) => {
    if (i == null || review) return;
    if (items[i].bucket === buckets[b]) {
      setPlaced((p) => ({ ...p, [i]: b }));
      setPicked(null);
    } else {
      setTries((t) => addTry(t, i, buckets[b]));
      setPicked(null);
      setWrong(i);
      setTimeout(() => setWrong(null), 400);
    }
  };

  return (
    <s.BucketsWrap>
      <s.Pool>
        {order
          .filter((i) => !isIn(i))
          .map((i) => (
            <s.Item
              key={i}
              type="button"
              draggable
              onDragStart={(e) => e.dataTransfer.setData("text/plain", String(i))}
              onClick={() => setPicked(picked === i ? null : i)}
              $picked={picked === i}
              $wrong={wrong === i}
            >
              {items[i].text}
            </s.Item>
          ))}
      </s.Pool>
      <s.Buckets $count={buckets.length}>
        {buckets.map((b, bi) => (
          <s.Bucket
            key={b}
            $i={bi}
            $over={over === bi}
            onClick={() => drop(picked, bi)}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(bi);
            }}
            onDragLeave={() => setOver(null)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(null);
              drop(Number(e.dataTransfer.getData("text/plain")), bi);
            }}
          >
            <h4>{b}</h4>
            <div>
              {items.map((it, i) =>
                isIn(i) && it.bucket === b ? (
                  <s.Placed key={i} $late={!showAnswers && review && (tries[i] || []).length > 0}>
                    {it.text}
                    {!showAnswers && review && (tries[i] || []).length > 0 && <s.TryList title="Неправильні спроби учня">{tries[i].join(" · ")}</s.TryList>}
                  </s.Placed>
                ) : null
              )}
            </div>
          </s.Bucket>
        ))}
      </s.Buckets>
    </s.BucketsWrap>
  );
}

// ---------- знайди помилку: type "find_mistake", data.lines = [{ text, wrong, fix }] ----------
// Клік по слову: якщо це помилка — закреслюється й поруч зʼявляється правильний варіант; ні — слово трясеться.
const bare = (w) => w.replace(/^[^\w']+|[^\w']+$/g, "").toLowerCase();
function FindMistake({ slide, showAnswers }) {
  const { lines = [] } = slide.data || {};
  const [found, setFound] = useSlideState(slide.id, "found", {});
  const [tries, setTries] = useSlideState(slide.id, "tries", {});
  const review = useReviewMode();
  useReport(slide.id, Object.keys(found).length, lines.length, Object.keys(found).length);
  const [bad, setBad] = useState(null);
  return (
    <s.Mistakes>
      {lines.map((line, li) => {
        const words = line.text.split(" ");
        const wrongWords = line.wrong.split(" ");
        const start = words.findIndex((_, k) => wrongWords.every((w, d) => bare(words[k + d] || "") === bare(w)));
        const done = showAnswers || found[li];
        return (
          <s.MistakeLine key={li}>
            <b>{li + 1}</b>
            {words.map((w, k) => {
              const inWrong = k >= start && k < start + wrongWords.length;
              if (done && inWrong) {
                if (k !== start) return null;
                return (
                  <s.Word key={k} as="span" $state="fixed" $late={!showAnswers && review && (tries[li] || []).length > 0}>
                    <s>{line.wrong}</s>
                    <b>{line.fix}</b>
                    {/[.,?!]$/.test(words[start + wrongWords.length - 1]) && words[start + wrongWords.length - 1].slice(-1)}
                  </s.Word>
                );
              }
              return (
                <s.Word
                  key={k}
                  type="button"
                  $state={bad === `${li}-${k}` ? "bad" : null}
                  onClick={() => {
                    if (done || review) return;
                    if (inWrong) setFound((f) => ({ ...f, [li]: true }));
                    else {
                      setTries((t) => addTry(t, li, w));
                      setBad(`${li}-${k}`);
                      setTimeout(() => setBad(null), 400);
                    }
                  }}
                >
                  {w}
                </s.Word>
              );
            })}
            {!showAnswers && review && (tries[li] || []).length > 0 && <s.TryList title="Неправильні спроби учня">{tries[li].join(" · ")}</s.TryList>}
          </s.MistakeLine>
        );
      })}
    </s.Mistakes>
  );
}

// ---------- talk з фото на весь фон: data.bg = true + slide.image ----------
export function BgTalk({ slide }) {
  const { questions = [] } = slide.data || {};
  return (
    <s.BgTalk $src={mediaUrl(slide.image.url)}>
      <s.BgCards>
        {questions.map((q) => (
          <div key={q.text}>{q.text}</div>
        ))}
      </s.BgCards>
    </s.BgTalk>
  );
}

export const RICH_BLOCKS = {
  facts: Facts,
  buckets: Buckets,
  find_mistake: FindMistake,
};
